import { useEffect } from "react"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import {
  $createParagraphNode,
  $createTextNode,
  $getAdjacentNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  KEY_ENTER_COMMAND,
  PASTE_COMMAND,
} from "lexical"
import {
  $createMergeTagNode,
  $isMergeTagNode,
  $removeMergeTagByKey,
  type MergeTagNode,
} from "./MergeTagNode"
import { serializeMergeTagEditorToPlainText } from "./serialize"
import { parseSegments, findLabel } from "./utils"

type InitPluginProps = {
  fieldValue: string
  forms: { externalId: string; name: string }[]
}

const $forEachFormMergeTag = (
  callback: (node: MergeTagNode) => void,
): void => {
  $getRoot().getChildren().forEach((paragraph) => {
    if (!$isElementNode(paragraph)) return

    paragraph.getChildren().forEach((child) => {
      if ($isMergeTagNode(child) && child.getTagId().startsWith("FORM_")) {
        callback(child)
      }
    })
  })
}

const $formTagLabelsNeedUpdate = (
  forms: { externalId: string; name: string }[],
): boolean => $getRoot().getChildren().some((paragraph) => {
  if (!$isElementNode(paragraph)) return false

  return paragraph.getChildren().some((child) => {
    if (!$isMergeTagNode(child) || !child.getTagId().startsWith("FORM_")) return false

    return child.getTagLabel() !== findLabel(child.getTagId(), forms)
  })
})

const $updateFormTagLabels = (
  forms: { externalId: string; name: string }[],
): void => {
  $forEachFormMergeTag((child) => {
    const desiredLabel = findLabel(child.getTagId(), forms)
    if (child.getTagLabel() !== desiredLabel) {
      child.replace($createMergeTagNode(child.getTagId(), desiredLabel))
    }
  })
}

const $populateEditorFromValue = (
  value: string,
  forms: { externalId: string; name: string }[],
): void => {
  const root = $getRoot()
  root.clear()
  const paragraph = $createParagraphNode()
  parseSegments(value).forEach((segment) => {
    if (segment.type === "text") {
      if (segment.value) {
        paragraph.append($createTextNode(segment.value))
      }
    } else {
      const label = findLabel(segment.value, forms)
      paragraph.append($createMergeTagNode(segment.value, label))
    }
  })
  root.append(paragraph)
}

export const MergeTagInitPlugin = ({ fieldValue, forms }: InitPluginProps) => {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    const value = fieldValue ?? ""
    const hasFormTags = /\{\{FORM_/.test(value)

    let shouldSync = false
    let shouldUpdateFormLabels = false

    editor.getEditorState().read(() => {
      const serialized = serializeMergeTagEditorToPlainText()
      shouldSync = serialized !== value
      if (!shouldSync && hasFormTags && forms.length > 0) {
        shouldUpdateFormLabels = $formTagLabelsNeedUpdate(forms)
      }
    })

    if (!shouldSync && !shouldUpdateFormLabels) return

    editor.update(() => {
      if (!shouldSync && shouldUpdateFormLabels) {
        $updateFormTagLabels(forms)

        return
      }

      $populateEditorFromValue(value, forms)
    })
  }, [editor, fieldValue, forms])

  return null
}

const handleMergeTagDelete = (backward: boolean): boolean => {
  const selection = $getSelection()

  if ($isNodeSelection(selection)) {
    const node = selection.getNodes()[0]
    if ($isMergeTagNode(node)) {
      $removeMergeTagByKey(node.getKey())

      return true
    }
  }

  if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
    return false
  }

  const { anchor } = selection
  const anchorNode = anchor.getNode()

  if ($isMergeTagNode(anchorNode)) {
    $removeMergeTagByKey(anchorNode.getKey())

    return true
  }

  const adjacentNode = $getAdjacentNode(anchor, backward)
  if ($isMergeTagNode(adjacentNode)) {
    $removeMergeTagByKey(adjacentNode.getKey())

    return true
  }

  return false
}

export const MergeTagKeyboardPlugin = () => {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    const unregisterBackspace = editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      () => handleMergeTagDelete(true),
      COMMAND_PRIORITY_HIGH,
    )

    const unregisterDelete = editor.registerCommand(
      KEY_DELETE_COMMAND,
      () => handleMergeTagDelete(false),
      COMMAND_PRIORITY_HIGH,
    )

    return () => {
      unregisterBackspace()
      unregisterDelete()
    }
  }, [editor])

  return null
}

export const SingleLinePlugin = () => {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    const unregisterEnter = editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        event?.preventDefault()

        return true
      },
      COMMAND_PRIORITY_HIGH,
    )

    const unregisterPaste = editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        if (!(event instanceof ClipboardEvent)) return false

        const clipboardText = event.clipboardData?.getData("text/plain")
        if (!clipboardText) return false

        event.preventDefault()
        const singleLine = clipboardText.replace(/\r?\n/g, " ")
        editor.update(() => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            selection.insertText(singleLine)
          }
        })

        return true
      },
      COMMAND_PRIORITY_HIGH,
    )

    return () => {
      unregisterEnter()
      unregisterPaste()
    }
  }, [editor])

  return null
}
